import { useState, useRef } from "react";
import React from "react";
import { useClickOutside } from "./useClickOutside"; // path where you save the hook

export default function Leaderboard({ leaderboard, currentTeamId, league, leagueRewards = [] }) {
  const [expandedUserId, setExpandedUserId] = useState(null);
  const profileRef = useRef();
  const [showRewardsModal, setShowRewardsModal] = useState(false);

  // Close profile when clicking outside
  useClickOutside(profileRef, () => setExpandedUserId(null));

  const toggleProfile = (userId) => {
    setExpandedUserId((prev) => (prev === userId ? null : userId));
  };

  const rewardsByPlacement = React.useMemo(() => {
    return Object.fromEntries(leagueRewards.map((r) => [r.placement, r]));
  }, [leagueRewards]);

  const getTrophyImage = (region) => {
    switch (region) {
      case "LCK":
        return "/banners/lck-trophy-clear.png";
      case "LPL":
        return "/banners/lpl-trophy-clear.png";
      case "LCP":
        return "/banners/lcp-trophy-clear.png";
      case "LEC":
        return "/banners/lec-trophy-clear.png";
      case "FIRST":
        return "/banners/first-trophy-clear.png";
      case "MSI":
        return "/banners/msi-trophy-clear.png";
      case "WORLDS":
        return "/banners/worlds-trophy-clear.png";
      default:
        return null;
    }
  };

  return (
    <div className="leaderboard-container">
      {/* Header */}
      <div className="leaderboard-header">
        <h2>STANDINGS</h2>

        {!league.is_private && (
          <button className="mobile-rewards-btn" onClick={() => setShowRewardsModal(true)}>
            &#9432; REWARDS
          </button>
        )}
      </div>

      {/* Leaderboard Content */}
      <div className="leaderboard-box">
        {leaderboard.length > 0 ? (
          <div className="leaderboard-list">
            {leaderboard.map((player, index) => {
              const reward = rewardsByPlacement[player.league_rank];
              return (
                <React.Fragment key={player.fantasy_team_id}>
                  <div className="leaderboard-row">
                    <div className={`leaderboard-dot ${player.league_rank <= 3 ? `rank-${player.league_rank}` : ""}`}></div>
                    <div className="left" onClick={() => toggleProfile(player.fantasy_team_id)} style={{ cursor: "pointer" }}>
                      {player.profile_image && <img src={`/avatars/${player.profile_image}`} alt={player.display_name} />}

                      <span className={`name ${player.fantasy_team_id === currentTeamId ? "you-highlight" : ""}`}>{player.display_name}</span>
                      {/* Expanded profile dropdown */}
                      {expandedUserId === player.fantasy_team_id && (
                        <div className="user-profile-group-display" ref={profileRef}>
                          <div style={{ display: "flex", gap: "6px" }}>
                            {player.profile_image && (
                              <img
                                src={`/avatars/${player.profile_image}`}
                                alt={player.display_name}
                                style={{
                                  width: "64px",
                                  height: "64px",
                                  border: "1px solid rgb(255 255 255 / 100%)",
                                }}
                              />
                            )}

                            <div>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "start",
                                  flexDirection: "column",
                                  gap: "10px",
                                }}>
                                <span className={`name ${player.fantasy_team_id === currentTeamId ? "you-highlight" : ""}`}>
                                  {player.display_name}
                                </span>
                                <div>
                                  <h2
                                    style={{
                                      margin: "0px",
                                      fontWeight: "400",
                                      fontSize: "10px",
                                      letterSpacing: "0px",
                                      color: "rgba(255, 255, 255, 0.6)",
                                    }}>
                                    Biggest fan of:
                                  </h2>

                                  <p
                                    style={{
                                      fontSize: "11px",
                                      color: "rgba(255, 255, 255, 1)",
                                      margin: "0",
                                      display: "flex",
                                      alignItems: "center",
                                    }}>
                                    <img
                                      src={`/teams/${player.favorite_team_logo}`}
                                      alt={player.favorite_team_name}
                                      style={{
                                        width: "16px",
                                        height: "16px",
                                        objectFit: "contain",
                                        marginRight: "2px",
                                        border: "none",
                                        borderRadius: "0",
                                      }}
                                    />
                                    {player.favorite_team_name}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>

                          <div
                            style={{
                              padding: "4px",
                              backgroundColor: "rgb(255 255 255 / 10%)",
                              borderRadius: "4px",
                              boxSizing: "border-box",
                              display: "flex",
                              marginTop: "10px",
                              alignItems: "center",
                            }}>
                            {player.user_display_badges?.length > 0 ? (
                              player.user_display_badges.map((badge, index) => (
                                <img
                                  key={index}
                                  src={`/badges/${badge.badge_image}`}
                                  alt={badge.badge_name}
                                  title={badge.badge_name}
                                  className="badge-icon"
                                  style={{
                                    width: "42px",
                                    height: "42px",
                                    border: "none",
                                    objectFit: "contain",
                                    minWidth: "42px",
                                    minHeight: "42px",
                                    transform: "translateZ(0)",
                                    webkitTransform: "translateZ(0)",
                                    backfaceVisibility: "hidden",
                                    flexShrink: "0",
                                  }}
                                />
                              ))
                            ) : (
                              <span style={{ fontSize: "12px", color: "#aaa" }}>No badges currently displayed</span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="points">
                      <div className="leaderboard-rewards-container">
                        {!league.is_private && rewardsByPlacement[player.league_rank]?.coins > 0 && (
                          <div
                            title={`+${rewardsByPlacement[player.league_rank].coins} coins`}
                            style={{ display: "flex", alignItems: "center", flexDirection: "column", marginTop: "-8px" }}>
                            <img src="/many-tokens.png" style={{ height: "24px" }} alt="Credit Token" />
                            <span
                              style={{
                                fontSize: "14px",
                                color: "white",
                                margin: "-10px 0",
                                textShadow: "rgba(0, 0, 0, 0.8) 0px 0px 8px",
                                fontWeight: "900",
                              }}>
                              {rewardsByPlacement[player.league_rank].coins}
                            </span>
                          </div>
                        )}
                        {!league.is_private && reward?.grants_international_ticket && (
                          <img src={reward.ticket_image || "/international-ticket.png"} alt="International Ticket" />
                        )}
                        {!league.is_private && reward?.player_skins && (
                          <img src={`/players/${reward.player_skins.full_preview}`} alt={reward.player_skins.name} className="reward-skin-preview" />
                        )}
                        {!league.is_private && player.league_rank === 1 && (
                          <img src={getTrophyImage(league.region || league.event_code)} alt="League Champion" style={{ height: "32px" }} />
                        )}
                        {!league.is_private && reward?.xp_boost && (
                          <div style={{ display: "flex", alignItems: "center", flexDirection: "column", marginTop: "-20px" }}>
                            <img src="/single_boost.png" alt="XP Boost" style={{ height: "36px" }} />
                            <span
                              style={{
                                fontSize: "14px",
                                color: "white",
                                margin: "-18px 0",
                                textShadow: "rgba(0, 0, 0, 1) 0px 0px 8px",
                                fontWeight: "900",
                              }}>
                              {reward?.xp_boost}
                            </span>
                          </div>
                        )}
                      </div>
                      {player.wins}W - {player.losses}L {player.ties > 0 ? `- ${player.ties}T` : ""}{" "}
                      <p style={{ fontWeight: "400", fontSize: "10px", margin: "0", color: "#ffffff99", textAlign: "right" }}>|</p>{" "}
                      <p style={{ fontWeight: "400", fontSize: "12px", margin: "0", color: "#ffffff99", textAlign: "right" }}>
                        {player.total_points_scored.toFixed(1)}
                      </p>{" "}
                      <p style={{ fontWeight: "400", fontSize: "10px", margin: "0", color: "#ffffff99", textAlign: "right" }}>pts</p>
                    </div>
                  </div>

                  {/* Divider after rank 4 */}
                  {index === 3 && (
                    <div className="qualify-divider">
                      <span>TOP 4 EARN INTERNATIONAL TICKET</span>
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        ) : (
          <div style={{ textAlign: "center" }}>
            <img src="/4.webp" alt="empty" style={{ opacity: ".2", maxWidth: "130px" }} />
            <p
              style={{
                color: "rgb(83, 94, 108)",
                fontSize: "14px",
                textAlign: "center",
              }}>
              We've got our best guy on it as we speak!
              <br />
              Right Rammus?
            </p>
          </div>
        )}
      </div>
      {showRewardsModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>League Rewards</h3>
              <button onClick={() => setShowRewardsModal(false)}>✕</button>
            </div>

            <div className="rewards-list">
              {leagueRewards
                .sort((a, b) => a.placement - b.placement)
                .map((reward) => (
                  <div key={reward.placement} className="reward-row">
                    <div className="placement">#{reward.placement}</div>

                    <div className="reward-items">
                      {reward.coins > 0 && (
                        <div className="reward-item">
                          <img src="/many-tokens.png" alt="Coins" />
                          <span>{reward.coins}</span>
                        </div>
                      )}

                      {reward.grants_international_ticket && (
                        <img src={reward.ticket_image || "/international-ticket.png"} alt="Ticket" className="ticket" />
                      )}
                      {reward.xp_boost && <img src="/single_boost.png" alt="XP Boost" className="ticket" />}
                      {reward.placement === 1 && !league.is_private && (
                        <img src={getTrophyImage(league.region)} alt="Champion Trophy" className="ticket" />
                      )}
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
